/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const sheetSizes = [
  { label: "4' x 8' (32 sq ft)", sqft: 32 },
  { label: "4' x 10' (40 sq ft)", sqft: 40 },
  { label: "4' x 12' (48 sq ft)", sqft: 48 },
];

export function DrywallCalculatorClient() {
  const [roomLength, setRoomLength] = useState("");
  const [roomWidth, setRoomWidth] = useState("");
  const [wallHeight, setWallHeight] = useState("9");
  const [numDoors, setNumDoors] = useState("1");
  const [numWindows, setNumWindows] = useState("2");
  const [includeCeiling, setIncludeCeiling] = useState(false);
  const [sheetSizeIdx, setSheetSizeIdx] = useState(0);
  const [pricePerSheet, setPricePerSheet] = useState("");

  const doorArea = 21; // 3x7
  const windowArea = 15; // 3x5
  const wastePercent = 0.12; // 12% waste for drywall

  const l = parseFloat(roomLength) || 0;
  const w = parseFloat(roomWidth) || 0;
  const h = parseFloat(wallHeight) || 9;
  const doors = parseFloat(numDoors) || 0;
  const windows = parseFloat(numWindows) || 0;

  const wallArea = 2 * (l + w) * h;
  const ceilingArea = includeCeiling ? l * w : 0;
  const openingsArea = doors * doorArea + windows * windowArea;
  const netArea = Math.max(0, wallArea + ceilingArea - openingsArea);
  const grossArea = netArea * (1 + wastePercent);

  const sheet = sheetSizes[sheetSizeIdx];
  const sheetSqFt = sheet?.sqft ?? 32;
  const sheetsNeeded = Math.ceil(grossArea / sheetSqFt);

  // Joint compound: ~0.053 gallons per sq ft (1 gallon per ~375 sq ft with 3 coats)
  const jointCompoundGallons = Math.ceil(grossArea / 375);
  // Joint tape: 1 roll (500 ft) per ~500 sq ft
  const tapeRolls = Math.ceil(grossArea / 500);
  // Screws: ~28 per sheet
  const screws = sheetsNeeded * 28;
  const screwBoxes = Math.ceil(screws / 200); // 200 per box typical

  const pps = pricePerSheet;
  const price = parseFloat(pps) || 0;

  const hasResult = netArea > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const inputClass = "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-foreground">Room Length (ft)</label>
          <input type="number" value={roomLength} onChange={(e) => setRoomLength(e.target.value)} placeholder="14" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Room Width (ft)</label>
          <input type="number" value={roomWidth} onChange={(e) => setRoomWidth(e.target.value)} placeholder="12" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Wall Height (ft)</label>
          <input type="number" value={wallHeight} onChange={(e) => setWallHeight(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Doors</label>
          <input type="number" value={numDoors} onChange={(e) => setNumDoors(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Windows</label>
          <input type="number" value={numWindows} onChange={(e) => setNumWindows(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Sheet Size</label>
          <select value={sheetSizeIdx} onChange={(e) => setSheetSizeIdx(Number(e.target.value))} className={inputClass}>
            {sheetSizes.map((s, i) => (
              <option key={s.label} value={i}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={includeCeiling} onChange={(e) => setIncludeCeiling(e.target.checked)} className="h-4 w-4 rounded border-input text-primary" />
            Include ceiling
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Price per Sheet (optional)</label>
          <input type="number" value={pricePerSheet} onChange={(e) => setPricePerSheet(e.target.value)} placeholder="14.00" className={inputClass} />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Net Area</div>
              <div className="text-2xl font-bold text-foreground">{fmt(netArea)} sq ft</div>
              {includeCeiling && <div className="text-xs text-muted-foreground">Walls + ceiling</div>}
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Sheets Needed</div>
              <div className="text-2xl font-bold text-secondary">{sheetsNeeded}</div>
              <div className="text-xs text-muted-foreground">incl. 12% waste</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Joint Compound</div>
              <div className="text-2xl font-bold text-foreground">{jointCompoundGallons} gal</div>
            </div>
            {price > 0 && (
              <div>
                <div className="text-sm text-muted-foreground">Sheet Cost</div>
                <div className="text-2xl font-bold text-foreground">${(sheetsNeeded * price).toFixed(2)}</div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-border p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Full Materials List</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Drywall Sheets ({sheet?.label.split("(")[0]?.trim()})</span>
                <span className="font-medium text-foreground">{sheetsNeeded}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Joint Compound</span>
                <span className="font-medium text-foreground">{jointCompoundGallons} gal</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Joint Tape</span>
                <span className="font-medium text-foreground">{tapeRolls} roll{tapeRolls > 1 ? "s" : ""}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Drywall Screws</span>
                <span className="font-medium text-foreground">~{fmt(screws)} ({screwBoxes} box{screwBoxes > 1 ? "es" : ""})</span>
              </div>
            </div>
          </div>
          <ResultActions
            toolSlug="drywall-calculator"
            inputs={{ roomLength, roomWidth, wallHeight, numDoors, numWindows, includeCeiling }}
            outputs={{ sheetsNeeded, jointCompoundGallons, tapeRolls, screwBoxes, netArea, grossArea }}
          />
        </>
      )}
    </div>
  );
}
